<script>
  import ClampText from "$lib/Elements/ClampText.svelte";

  // Structured Lua error, matching the wire shape from Lua.VM.ErrorFormatter.to_map/3
  // (runtime errors) and Lua.Parser.Error.to_map/2 (parse errors):
  // { type, message, source, line, call_stack, source_context, suggestion, error_kind }
  export let error = null;
  // Compact variant (event stream rows): omits the source-context block.
  export let compact = false;
  // Optional callback (line) => void. When provided, the location renders as a
  // button that jumps to the offending line in the code view.
  export let onJumpToLine = null;

  const TYPE_LABELS = {
    // Runtime errors (Lua.VM.ErrorFormatter)
    runtime_error: "Runtime Error",
    type_error: "Runtime Type Error",
    assertion_error: "Assertion Error",
    argument_error: "Argument Error",
    // Parse errors (Lua.Parser.Error)
    parse_error: "Syntax Error",
    unexpected_token: "Syntax Error",
    unexpected_end: "Syntax Error",
    expected_token: "Syntax Error",
    unclosed_delimiter: "Syntax Error",
    invalid_syntax: "Syntax Error",
    lexer_error: "Syntax Error",
    multiple_errors: "Syntax Error",
  };

  $: typeLabel = TYPE_LABELS[error?.type] ?? "Lua Error";

  $: location =
    error?.source && error?.line
      ? `${error.source}:${error.line}`
      : error?.source
        ? error.source
        : error?.line
          ? `line ${error.line}`
          : null;

  $: sourceContext = error?.source_context ?? null;
  $: stack = error?.call_stack ?? [];

  function pointer(line) {
    const column = line?.pointer_column ?? sourceContext?.pointer_column ?? 1;
    return " ".repeat(Math.max(0, column - 1)) + "^";
  }

  function frameLabel(frame) {
    return frame.name ? `in function '${frame.name}'` : "in main chunk";
  }
</script>

{#if error}
  <div
    class="rounded-md overflow-hidden border border-red-900/50 bg-gray-900 text-xs font-mono not-prose"
  >
    <div class="bg-red-900/40 text-red-300 font-bold px-3 py-1.5">
      {typeLabel}
    </div>

    <div class="p-3 space-y-2">
      {#if location}
        {#if onJumpToLine && error?.line}
          <button
            type="button"
            on:click={() => onJumpToLine(error.line)}
            class="group inline-flex items-center gap-1 text-content-subtle hover:text-gray-200 focus:outline-none focus:text-gray-200"
          >
            <span class="underline decoration-dotted underline-offset-2">at {location}</span>
            <span class="transition-transform group-hover:translate-x-0.5">&rarr;</span>
          </button>
        {:else}
          <div class="text-content-subtle">at {location}</div>
        {/if}
      {/if}

      <div class="text-red-300 whitespace-pre-wrap break-words">{error.message}</div>

      {#if !compact && sourceContext?.lines?.length}
        <div class="mt-2 overflow-x-auto scrollbar-thin-dark rounded bg-gray-950/60 py-1.5">
          {#each sourceContext.lines as line}
            <div class="flex {line['highlight?'] ? 'text-red-400' : 'text-content-subtle'}">
              <span
                class="select-none text-right pr-3 w-10 shrink-0 text-content-muted tabular-nums"
                >{line.number}</span
              >
              <span class="whitespace-pre">{line.text}</span>
            </div>
            {#if line["highlight?"]}
              <div class="flex text-red-500 select-none">
                <span class="w-10 shrink-0"></span>
                <span class="whitespace-pre">{pointer(line)}</span>
              </div>
            {/if}
          {/each}
        </div>
      {/if}

      {#if stack.length > 0}
        <details class="mt-1">
          <summary class="cursor-pointer select-none text-content-subtle hover:text-gray-200">
            Stack trace ({stack.length})
          </summary>
          <div class="mt-1 space-y-0.5 pl-2 border-l border-gray-700">
            {#each stack as frame}
              <div class="text-content-subtle">
                <ClampText>
                  <span class="text-content-subtle">{frame.source ?? "?"}:{frame.line ?? "?"}</span>
                  <span class="ml-1 text-content-subtle">{frameLabel(frame)}</span>
                </ClampText>
              </div>
            {/each}
          </div>
        </details>
      {/if}

      {#if error.suggestion}
        <div
          class="mt-2 rounded border border-cyan-900/50 bg-cyan-950/30 px-2 py-1.5 text-cyan-300 whitespace-pre-wrap break-words"
        >
          <span class="font-bold">Suggestion: </span>{error.suggestion}
        </div>
      {/if}
    </div>
  </div>
{/if}
